import { useState, useEffect } from 'react';

interface NavbarProps {
  onBookCall: () => void;
}

export default function Navbar({ onBookCall }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', href: '#home' },
    { label: 'Services', href: '#services' },
    { label: 'Industries', href: '#industries' },
    { label: 'Results', href: '#results' },
    { label: 'Work', href: '#work' },
    { label: 'Contact', href: '#contact' },
  ];

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-black/95 backdrop-blur-md border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-28 py-2">

          {/* Logo — dominant brand element */}
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); handleNavClick('#home'); }}
            className="flex-shrink-0 flex items-center cursor-pointer"
          >
            <img
              src="https://storage.readdy-site.link/project_files/6a9ee8b5-3e14-4cac-83f4-7fb00d701629/1e96ec08-cdc0-4a03-9997-74d69b8440f9_cgoeight-trasnparent.png?v=7f1d19bcf2e06a33cfa1bc775f6a704b"
              alt="CGOeight Media"
              style={{ width: '210px', height: 'auto', maxHeight: '88px', objectFit: 'contain', objectPosition: 'left center', imageRendering: 'crisp-edges' }}
            />
          </a>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="text-sm font-medium text-white/60 hover:text-white transition-colors duration-200 tracking-wide cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <a
              href="tel:+19492146791"
              className="whitespace-nowrap px-5 py-2.5 text-sm font-semibold text-black bg-electric-blue rounded-md hover:bg-white transition-colors duration-300 glow-blue-hover tracking-wide cursor-pointer"
            >
              Book a Strategy Call
            </a>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
            aria-label="Toggle menu"
          >
            <div className="flex flex-col gap-1.5">
              <span className={`block h-px w-6 bg-white transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block h-px w-6 bg-white transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
              <span className={`block h-px w-6 bg-white transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-400 ${
          menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        } bg-black/98 border-b border-white/5`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {/* Mobile logo */}
          <img
            src="https://storage.readdy-site.link/project_files/6a9ee8b5-3e14-4cac-83f4-7fb00d701629/1e96ec08-cdc0-4a03-9997-74d69b8440f9_cgoeight-trasnparent.png?v=7f1d19bcf2e06a33cfa1bc775f6a704b"
            alt="CGOeight Media"
            style={{ width: '160px', height: 'auto', objectFit: 'contain' }}
          />
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
              className="text-base font-medium text-white/70 hover:text-white transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <a
            href="tel:+19492146791"
            onClick={() => setMenuOpen(false)}
            className="whitespace-nowrap mt-2 px-5 py-3 text-sm font-semibold text-black bg-electric-blue rounded-md cursor-pointer text-center"
          >
            Book a Strategy Call
          </a>
        </div>
      </div>
    </nav>
  );
}
